/* @flow */
import { action, computed, observable } from 'mobx';

import type { AssetComment } from 'models/Assets';

import { ApiModel } from '../api/ApiModel';
import { errorHandlerService } from '../services/ServiceRegistry';

export type AssetPricingModel = 'PERFECT' | 'PERFECT_SALE' | 'D365';

const assetUrls = {
  all: 'assets',
  asset: 'assets/',
  assetComments: (assetId: string) => `assets/${assetId}/comments`,
  deleteFile: (assetId, fileId) => `assets/${assetId}/documents/${fileId}`,
};

export type TypeAsset = {|
  asset_number: string,
  owned: boolean,
  id: string,
  account: {
    id: string,
    name: string,
    number: string,
    sale_consignment: boolean,
    subrental_program: boolean,
  },
  is_caas_asset: boolean,
  model: string,
  description: string,
  serial_number: string,
  status: string,
  software_version: string,
  firmware_version: string,
  product: string,
  manufacturer: string,
  calibration: {
    cert_order_number: string,
    certificate: string,
    status: string,
    due_date: string,
    interval: string,
    in_service_date: string,
    safety_tested: string,
  },
  replacement: ?{
    asset_id: string,
    in_use: boolean,
    model: string,
    asset_number: string,
    is_its_own_rpl: boolean,
    description: string,
    owned: boolean,
    er_product_type: string,
    manufacturer: string,
    date: string,
  },
  location: {
    building: {
      id: string,
      postcode: string,
      name: string,
      address: {
        id: string,
        address_1: string,
        address_2: string,
        address_3: string,
        address_4: string,
        address_5: string,
        postcode: string,
      },
      contact: string,
    },
    site: ?{
      address: ?{
        postcode: string,
        address_1: string,
        address_2: string,
        address_4: string,
      },
    },
  },
  user: {
    id: string,
    name: string,
    role: string,
    phone: string,
  },
  calibration_service: ?{
    id: string,
    type: ?string,
    price: ?number,
    status: ?string,
    vendor: ?string,
    asset_id: string,
    currency: ?string,
    created_at: string,
    is_active: boolean,
    deleted_at: ?string,
    updated_at: ?string,
    contact_name: ?string,
    observations: ?string,
    closure_date: ?string,
    contact_phone: ?string,
    collection_date: ?string,
    use_asset_contact: boolean,
    work_order_number: ?string,
    collection_address: ?string,
    is_rental_backfill: boolean,
    purchase_order_number: ?string,
    use_asset_collection_address: boolean,
  },
  sale_consignment: boolean,
  subrental_program: boolean,
  children: boolean,
  project: {
    name: string,
    code: string,
    start_date: string,
    end_date: string,
    location: {
      building: {
        id: string,
        postcode: string,
        address: string,
        contact: string,
      },
    },
    postcode: string,
    costcenter_name: string,
    costcenter_code: string,
  },
  disabled: boolean,
  contract: Object,
  weekly_rate: number,
  monthly_rate: number,
  option_fitted: Object,
  er_options_fitted_string: string, // For Rented Assets
  owned_options_fitted_text: string, // For Owned Assets
  pricing: {
    model: AssetPricingModel,
    currency: string,
  } & {
    model: 'PERFECT',
    rate: number,
    rate_code: null,
    transaction_type_description: null,
    sale_rate: number,
  } & {
    model: 'D365',
    rate: number,
    rate_code: string,
    transaction_type_description: string | null,
    sale_rate: null,
  } & {
    model: 'PERFECT_SALE',
    rate: null,
    rate_code: string,
    transaction_type_description: string | null,
    sale_rate: number,
  },
|};

export default class AssetStore extends ApiModel {
  @observable asset: ?TypeAsset;
  @observable assets: TypeAsset[] = [];
  @observable assetComments: {
    data?: AssetComment[],
    last_page?: number,
    current_page?: number,
  } = {};

  @observable loadingAsset = false;
  @observable loadingAssets = false;
  @observable loadingAssetComments = false;
  @observable loadingNewAssetComment = false;

  @observable assetActivity: ?Array<*> = null;

  @action
  async loadAsset(id: string, load: boolean = true): Promise<*> {
    this.loadingAsset = load;

    if (load) {
      this.clearDetails();
    }

    try {
      this.asset = await this.api.getResource(assetUrls.asset + id);
    } catch (e) {
      errorHandlerService.handleException(e);
    }

    this.loadingAsset = false;
  }

  @action
  async loadAssets(ids: string[]): Promise<*> {
    this.loadingAssets = true;

    try {
      const promises = ids.map(id => {
        return this.api.getResource(assetUrls.asset + id);
      });
      this.assets = await Promise.all(promises);
    } catch (e) {
      errorHandlerService.handleException(e);
    }

    this.loadingAssets = false;
  }

  @action
  async loadAssetActivity(id: string, params: ?string): Promise<*> {
    this.loadingAssetActivity = true;
    const urlParams: string = params ? '/activity' + params : '/activity';

    try {
      this.assetActivity = await this.api.getResource(assetUrls.asset + id + urlParams);
    } catch (e) {
      errorHandlerService.handleException(e);
    }

    // this.loadingAsset = false;
  }

  @action async loadAssetComments(id: string, pageNumber: number, persist?: boolean) {
    if (!persist) {
      this.assetComments = {};
    }
    this.loadingAssetComments = true;

    try {
      this.assetComments = await this.api.getPagedResource(
        `${assetUrls.assetComments(id)}?page_size=10&page=${pageNumber}`
      );
    } catch (e) {
      errorHandlerService.handleException(e);
    } finally {
      this.loadingAssetComments = false;
    }
  }

  @action async addAssetComment(id: string, comment: string) {
    this.loadingNewAssetComment = true;

    try {
      await this.api.createResource(assetUrls.assetComments(id), { comment });
    } catch (e) {
      errorHandlerService.handleException(e);
    } finally {
      this.loadingNewAssetComment = false;
    }
  }

  @action clearDetails() {
    this.asset = undefined;
    this.assets = undefined;
  }

  @action clearAssetStore() {
    this.clearDetails();
    this.assetComments = [];

    this.assetActivity = null;
    this.loadingAsset = true;
  }

  @computed get getCalibrationCer(): Array<Array<*>> {
    return (
      this.asset.calibration_certificates &&
      this.asset.calibration_certificates.map(cert => {
        if (cert.file) {
          return this.objReturn(cert.id, cert.name, cert.created_at, cert.file, cert.type, false);
        } else {
          return this.objReturn(
            cert.id,
            cert.er_certificate_number,
            cert.created_at,
            `assets/${cert.asset_id}/calibration-certificate/${cert.id}/download`,
            'PDF',
            true
          );
        }
      })
    );
  }

  @computed get getHisCalibrationCer(): Array<Array<*>> {
    return this.getCalibrationCer.filter((a, i) => i > 0);
  }

  @action
  async onDeleteFile(assetId, fileId) {
    try {
      const url = assetUrls.deleteFile(assetId, fileId);

      await this.api.deleteResource(url);
      await this.loadAsset(assetId, false);
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }
  }

  objReturn(
    id: string,
    name: string,
    createdAt: string,
    link: string,
    type: string,
    create: boolean
  ) {
    return {
      id,
      name,
      icon: '#icon-calibration-certificate',
      created_at: createdAt,
      link,
      type,
      showType: true,
      create,
      folderId: this.asset.id,
      onDelete: this.onDeleteFile.bind(this),
    };
  }
}
